import { Component, computed, effect, inject, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ColGroupDef,
  FirstDataRenderedEvent,
  GridSizeChangedEvent,
} from 'ag-grid-community';
import { forkJoin } from 'rxjs';
import { SessionService } from '../../core/session/session.service';
import { LocalizationService } from '../../core/services/localization.service';
import {
  TariffCheckService,
  AutoplanCheckResponse,
  ApiCalculatedPlan,
} from './tariff-check.service';
import { getCurrency } from '../../shared/utils/currency.util';
import {
  HlmIconComponent,
  HlmBadgeComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
  HlmCheckboxComponent,
} from '../../ui';

/** Auto-tariff plan names from old CMT */
const AUTO_TARIFF_NAMES = [
  'Tarifbestimmung',
  'Tariff determination',
  'definizione tariffa automatica',
  'Tarifa Automática',
  'Definizione tariffa',
  'Détermination tarifaire',
];

interface ExtensionToggle {
  name: string;
  showName: string;
  booked: boolean;
  error: string | null;
}

interface PlanSummary {
  planName: string;
  localizedName: string | null;
  totalBasicFeeSum: number;
  totalServiceFeeSum: number;
  totalSum: number;
  extensionsText: string;
}

@Component({
  selector: 'cmt-tariff-check',
  imports: [
    DecimalPipe,
    AgGridAngular,
    HlmIconComponent,
    HlmBadgeComponent,
    HlmSpinnerComponent,
    HlmAlertComponent,
    HlmCheckboxComponent,
  ],
  templateUrl: './tariff-check.component.html',
})
export class TariffCheckComponent {
  readonly session = inject(SessionService);
  private readonly tariffService = inject(TariffCheckService);
  private readonly localizationService = inject(LocalizationService);
  private initialized = false;

  readonly loading = signal(false);
  readonly loaded = signal(false);

  /** Raw API response */
  private readonly apiResult = signal<AutoplanCheckResponse | null>(null);

  /** Localization maps (loaded once on init) */
  private featureTranslations = new Map<string, string>();
  private planTranslations = new Map<string, string>();

  /** Extension toggles — merged from known extensions + API response */
  readonly extensions = signal<ExtensionToggle[]>([]);

  /** Included/excluded extension names for API calls */
  private includedExtensions: string[] = [];
  private excludedExtensions: string[] = [];
  private pendingExtension: ExtensionToggle | null = null;

  /** Known extensions (initial state before API response) */
  private readonly knownExtensions: ExtensionToggle[] = [
    { name: 'TarifErw2', showName: '24h extension', booked: false, error: null },
    { name: 'TarifErw1', showName: 'Extended Business hours extension', booked: false, error: null },
  ];

  /** Auto-tariff detection */
  readonly isAutoTariff = signal(false);
  readonly currentPlanName = signal('(unknown)');

  /** No active phone service tariff (plan not found in calculatedPlanList) */
  readonly noActivePhoneserviceTariff = signal(false);

  readonly hasPhoneService = computed(() => this.session.phoneServices().length > 0);
  readonly currency = computed(() => getCurrency(this.session.brandId() ?? 0));

  /** Comparison period — auto-calculated from API response or ~28 days back */
  readonly comparisonPeriodFrom = signal('');
  readonly comparisonPeriodTo = signal('');

  /** Current plan summary (selected from calculatedPlanList by currentPlanName) */
  readonly currentPlan = signal<PlanSummary | null>(null);

  /** Best plan summary (selected from calculatedPlanList by bestPlanName) */
  readonly bestPlan = signal<PlanSummary | null>(null);

  /** Sorted plan list for grid columns */
  readonly sortedPlans = signal<ApiCalculatedPlan[]>([]);

  readonly columnDefs = computed<(ColDef | ColGroupDef)[]>(() => {
    const plans = this.sortedPlans();
    if (plans.length === 0) return [];

    const res = this.apiResult();
    if (!res) return [];

    const bestName = res.bestPlanName;

    const fixedCols: ColDef[] = [
      {
        headerName: 'Tech name',
        field: 'featureKey',
        width: 150,
        cellClass: 'tech-feature',
      },
      { headerName: 'Feature', field: 'featureName', width: 300 },
      { headerName: 'Count', field: 'count', width: 64 },
    ];

    const planCols: ColGroupDef[] = plans.map((plan) => {
      const localizedName = this.planTranslations.get(plan.planName);
      return {
        headerName: localizedName ?? plan.planName,
        children: [
          {
            headerName: 'Unit price',
            field: `unitPrice${plan.level}`,
            width: 120,
            cellClass: this.buildCellClass(plan.planName, bestName, true),
            valueFormatter: (params: any) => this.formatCurrency(params.value),
          },
          {
            headerName: 'Sum',
            field: `sum${plan.level}`,
            width: 120,
            cellClass: this.buildCellClass(plan.planName, bestName, false),
            valueFormatter: (params: any) => {
              if (params.value != null) {
                return this.formatCurrency(params.value);
              }
              return '';
            },
          },
        ],
      };
    });

    return [...fixedCols, ...planCols];
  });

  /** Main grid row data — flattened features merged across plans (formatTopData) */
  readonly rowData = computed(() => {
    const plans = this.sortedPlans();
    if (plans.length === 0) return [];

    return this.formatTopData(plans);
  });

  /** Pinned bottom rows: Usage fees, Basic charges, Total */
  readonly pinnedBottomRowData = computed(() => {
    const plans = this.sortedPlans();
    if (plans.length === 0) return [];

    return [
      this.mergeBottomData({ featureName: 'Usage fees' }, 'sum', 'totalServiceFeeSum', plans),
      this.mergeBottomData({ featureName: 'Basic charges' }, 'sum', 'totalBasicFeeSum', plans),
      this.mergeBottomData({ featureName: 'Total' }, 'sum', 'totalSum', plans),
    ];
  });

  readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
  };

  constructor() {
    effect(() => {
      const customer = this.session.customer();
      // Both customer() and phoneServices() load in parallel. Only latch
      // `initialized` once we actually start the load — otherwise a customer()
      // that resolves before phoneServices() would latch the guard while
      // hasPhoneService() is still false, and the load would never fire when
      // phoneServices() arrives later (CLAUDE.md pitfall #13: derived state
      // from parallel async sources). The effect re-runs when either signal
      // changes, so it self-heals once both are present.
      if (customer && this.hasPhoneService() && !this.initialized) {
        this.initialized = true;
        this.setComparisonPeriod();
        this.initAndLoad();
      }
    });
  }

  /**
   * Calculate and set comparison period (~28 days = one billing period).
   * TODO: The API supports custom fromDate/toDate query params (currently unused,
   * same as old CMT which commented them out). Consider adding date pickers
   * to let users customize the comparison period in a future iteration.
   */
  private setComparisonPeriod(): void {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 28);
    this.comparisonPeriodFrom.set(from.toISOString().slice(0, 10));
    this.comparisonPeriodTo.set(today.toISOString().slice(0, 10));
  }

  /** Load translations then fetch tariff data */
  private initAndLoad(): void {
    const customer = this.session.customer();
    if (!customer) return;

    this.loading.set(true);

    // Load feature + plan translations in parallel, then fetch tariff check
    forkJoin({
      features: this.localizationService.getTranslations(
        customer.brandId, String(customer.mainCustomerLanguage), 'featureShowname', 'defTarif'
      ),
      plans: this.localizationService.getTranslations(
        customer.brandId, String(customer.mainCustomerLanguage), '', 'pricePackageName'
      ),
    }).subscribe({
      next: ({ features, plans }) => {
        this.featureTranslations = features;
        this.planTranslations = plans;
        this.checkAutoplan();
      },
      error: () => {
        // Translations failed — proceed without them
        this.checkAutoplan();
      },
    });
  }

  toggleExtension(ext: ExtensionToggle): void {
    // Clone and toggle
    const toggled: ExtensionToggle = { ...ext, booked: !ext.booked, error: null };
    this.pendingExtension = { ...toggled };

    // Update included/excluded
    this.manageIncludedExcludedExtensions(toggled);

    // Re-fetch
    this.checkAutoplan();
  }

  private checkAutoplan(): void {
    const customerId = this.session.customerId();
    if (customerId === null) return;

    this.loading.set(true);
    this.tariffService
      .checkAutoplan(customerId, this.includedExtensions, this.excludedExtensions)
      .subscribe({
        next: (response) => this.processResponse(response),
        error: () => {
          this.apiResult.set(null);
          this.sortedPlans.set([]);
          this.loaded.set(true);
          this.loading.set(false);
        },
      });
  }

  private processResponse(response: AutoplanCheckResponse): void {
    // Check if current plan is auto-tariff or not in plan list
    if (this.isNoPhoneserviceRelatedTariff(response)) {
      this.loaded.set(true);
      this.loading.set(false);
      return;
    }

    this.apiResult.set(response);

    // Sort plans by level ascending
    const plans = [...response.calculatedPlanList].sort((a, b) => a.level - b.level);
    this.sortedPlans.set(plans);

    // Select best plan
    const bestPlanData = plans.find((p) => p.planName === response.bestPlanName);
    if (bestPlanData) {
      this.bestPlan.set(this.toPlanSummary(bestPlanData));
    }

    // Select current plan (only if not auto-tariff, and only on first load)
    if (!this.isAutoTariff() && this.currentPlan() === null) {
      const currentPlanData = plans.find((p) => p.planName === response.currentPlanName);
      if (currentPlanData) {
        this.currentPlan.set(this.toPlanSummary(currentPlanData));
      }
    }

    // Merge extensions from API response
    this.mergeExtensionsFromResponse(plans);

    this.loaded.set(true);
    this.loading.set(false);
  }

  /** Replicates old CMT isNoPhoneserviceRelatedTariff */
  private isNoPhoneserviceRelatedTariff(response: AutoplanCheckResponse): boolean {
    this.currentPlanName.set('(unknown)');
    this.noActivePhoneserviceTariff.set(true);

    if (response?.currentPlanName) {
      const name = response.currentPlanName;
      this.currentPlanName.set(name);

      if (AUTO_TARIFF_NAMES.includes(name) || /auto/i.test(name)) {
        this.noActivePhoneserviceTariff.set(false);
        this.isAutoTariff.set(true);
        this.apiResult.set(null);
        this.sortedPlans.set([]);
        return true;
      }

      const found = response.calculatedPlanList.some((p) => p.planName === name);
      this.noActivePhoneserviceTariff.set(!found);
    }

    return this.noActivePhoneserviceTariff();
  }

  /** Build PlanSummary with localized name and extensions text */
  private toPlanSummary(plan: ApiCalculatedPlan): PlanSummary {
    const localizedName = this.planTranslations.get(plan.planName) ?? null;
    const extensionsText = this.buildExtensionsText(plan.extensionList);

    return {
      planName: plan.planName,
      localizedName,
      totalBasicFeeSum: plan.totalBasicFeeSum,
      totalServiceFeeSum: plan.totalServiceFeeSum,
      totalSum: plan.totalSum,
      extensionsText,
    };
  }

  /** Build extensions display string like old CMT setPlanExtensions */
  private buildExtensionsText(extensionList: { name: string; booked: boolean; showName: string }[]): string {
    const booked = extensionList.filter((e) => e.booked);
    if (booked.length === 0) return '(no active extensions)';

    return booked.map((e) => this.resolveExtensionShowName(e.name, e.showName)).join(', ');
  }

  /** Resolve extension display name from known extensions, fall back to API showName */
  private resolveExtensionShowName(name: string, fallback: string): string {
    const known = this.knownExtensions.find((k) => k.name === name);
    return known?.showName ?? fallback;
  }

  /**
   * Merge extensions from all plans with known extensions (old CMT mergeExtensions).
   * Also reset and rebuild included/excluded lists from API state.
   */
  private mergeExtensionsFromResponse(plans: ApiCalculatedPlan[]): void {
    // Collect all extensions from all plans
    const allApiExtensions = plans.flatMap((p) => p.extensionList);

    // Reset included/excluded from API response state
    this.includedExtensions = [];
    this.excludedExtensions = [];
    for (const ext of allApiExtensions) {
      this.manageIncludedExcludedExtensions(ext);
    }

    // Merge: start with known extensions, add any unknown from API
    const merged: ExtensionToggle[] = this.knownExtensions.map((k) => ({ ...k, error: null }));

    for (const apiExt of allApiExtensions) {
      const existing = merged.find((m) => m.name === apiExt.name);
      if (existing) {
        if (apiExt.booked) {
          existing.booked = true;
        }
      } else {
        merged.push({
          name: apiExt.name,
          showName: apiExt.showName ?? apiExt.name,
          booked: apiExt.booked,
          error: null,
        });
      }
    }

    // Analyse pending extension request (error if toggle didn't take effect)
    if (this.pendingExtension) {
      const found = merged.find((m) => m.name === this.pendingExtension!.name);
      if (found && found.booked !== this.pendingExtension.booked) {
        found.error = `The extension '${this.resolveExtensionShowName(found.name, found.showName)}' cannot be booked`;
      }
      this.pendingExtension = null;
    }

    this.extensions.set(merged);
  }

  /** Manage included/excluded arrays based on extension state */
  private manageIncludedExcludedExtensions(ext: { name: string; booked: boolean }): void {
    this.includedExtensions = this.includedExtensions.filter((n) => n !== ext.name);
    this.excludedExtensions = this.excludedExtensions.filter((n) => n !== ext.name);

    if (ext.booked) {
      this.includedExtensions.push(ext.name);
    } else {
      this.excludedExtensions.push(ext.name);
    }
  }

  /**
   * Replicate old CMT formatTopData:
   * Flatten all features from all plans, merge by featureKey+count into single rows
   * with unitPrice{level} and sum{level} columns per plan.
   */
  private formatTopData(plans: ApiCalculatedPlan[]): Record<string, any>[] {
    return plans
      .flatMap((plan) =>
        plan.featureList.map((feature) => {
          const translation = this.featureTranslations.get(feature.key);
          const row: Record<string, any> = {
            featureKey: feature.key,
            featureName: translation ?? feature.showName,
            count: feature.amount,
          };
          row[`unitPrice${plan.level}`] = feature.price;
          row[`sum${plan.level}`] = feature.totalSum;
          return row;
        })
      )
      .reduce<Record<string, any>[]>((acc, current) => {
        const existing = acc.find(
          (row) => row['featureKey'] === current['featureKey'] && row['count'] === current['count']
        );
        if (existing) {
          Object.assign(existing, current);
        } else {
          acc.push(current);
        }
        return acc;
      }, []);
  }

  /**
   * Replicate old CMT mergeBottomData:
   * Create a summary row with unitPrice{level}='-' and sum{level}=plan[fieldName].
   */
  private mergeBottomData(
    initial: Record<string, any>,
    prefixKey: string,
    fieldName: string,
    plans: ApiCalculatedPlan[]
  ): Record<string, any> {
    return plans.reduce((row, plan) => {
      row[`unitPrice${plan.level}`] = '-';
      row[`${prefixKey}${plan.level}`] = (plan as any)[fieldName];
      return row;
    }, initial);
  }

  /** Build cell class function matching old CMT showIfBestPlan */
  private buildCellClass(
    planName: string,
    bestPlanName: string,
    isSeparator: boolean
  ): () => string[] {
    return () => {
      const classes = ['align-content-cell'];
      if (isSeparator) {
        classes.push('tariff-separator');
      }
      if (planName === bestPlanName) {
        classes.push('best-plan-cell');
      }
      return classes;
    };
  }

  /** Format currency value — replicates old CMT onFormatNumber */
  private formatCurrency(value: any): string {
    if (value === '-') return '-';
    if (value != null && typeof value === 'number') {
      return `${this.currency()} ${value.toFixed(2)}`;
    }
    return 'Not supported';
  }

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    event.api.sizeColumnsToFit();
  }

  onGridSizeChanged(event: GridSizeChangedEvent): void {
    event.api.sizeColumnsToFit();
  }
}
